import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { CancelButton } from '../../../irmlib/src/components/FormControls/Button'
import { t } from 'i18next'
// import { Box } from '@material-ui/core'
import Camera from 'react-html5-camera-photo'

import {
  Stage,
  Layer,
  Rect,
  Text,
  Arrow,
  Image as KonvaImage,
  Transformer,
  Group,
} from 'react-konva'
import Cropper from 'react-cropper'
import CropIcon from '@mui/icons-material/Crop'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import AbcIcon from '@mui/icons-material/Abc'
import Crop169Icon from '@mui/icons-material/Crop169'
import CropFreeIcon from '@mui/icons-material/CropFree'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import UndoIcon from '@mui/icons-material/Undo'
import HighlightAltIcon from '@mui/icons-material/HighlightAlt'
import RedoIcon from '@mui/icons-material/Redo'
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate'
import DeleteIcon from '@mui/icons-material/Delete'
import { clearBusy, setBusy } from '../../../irmlib/src/containers'
import { createPortal } from 'react-dom'
// import { t } from 'i18next'
import Resizer from 'react-image-file-resizer'
import { ContainedButton, OutlinedButton } from '../../../irmlib/src/components/Controls'
import { doesFileSizeExceed } from './UploadButton'
import { Fade, IconButton, Tooltip } from '@mui/material'

// Utilities:
const uid = () => Math.random().toString(36).slice(2, 9)
function rectsIntersect(a, b) {
  return !(
    a.x > b.x + b.width ||
    a.x + a.width < b.x ||
    a.y > b.y + b.height ||
    a.y + a.height < b.y
  )
}
// Component:
const ReferenceImage = (props) => {
  const { open, onClose, onImageCapture, maxSize, allowedFormats, setSelectedImageLabel, showEditScreen, updateRefState } = props
  const [originalImage, setOriginalImage] = useState(null)
  const [activeTab, setActiveTab] = useState('upload')
  const [mode, setMode] = useState('upload')
  // const cameraReff = useRef(null)
  const [error, setError] = useState('')
  const cameraLoaderRef = useRef(false)

  const width = 1000
  const height = 800
  // refs
  const stageRef = useRef()
  const layerRef = useRef()
  const trRef = useRef()
  const containerRef = useRef()
  // items: unified array of shapes (rect | arrow | text)
  const [items, setItems] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [tool, setTool] = useState(null) // 'rect' | 'arrow' | 'text' | null
  const [editingText, setEditingText] = useState(false)
  // drawing & marquee
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawing, setDrawing] = useState(null) // { id, type, startX, startY }
  const [marquee, setMarquee] = useState(null) // {x,y,w,h,visible}
  // styling / toolbar
  const [color, setColor] = useState('#FF0000')
  const [fontSize, setFontSize] = useState(20)
  const [scale, setScale] = useState(1)
  const baseScaleRef = useRef(1)
  // history for undo/redo
  const maxHistory = 1000
  const [history, setHistory] = useState([])

  const [originalFile, setOriginalFile] = useState(null)
  const [workingImage, setWorkingImage] = useState(null)
  const [isCropping, setIsCropping] = useState(false)
  const [cropData, setCropData] = useState(null)
  const cropperRef = useRef(null)
  const [cropInstance, setCropInstance] = useState(null)

  const [histIndex, setHistIndex] = useState(-1)
  const isMountedRef = useRef(true)

  // Track component mount status
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Calculate image dimensions to maintain aspect ratio
  const imageDimensions = useMemo(() => {
    if (!workingImage) return { x: 0, y: 0, width: 0, height: 0 }
    
    // Try to get image dimensions from various sources
    const imgWidth = workingImage.naturalWidth || workingImage.width || 0
    const imgHeight = workingImage.naturalHeight || workingImage.height || 0
    
    // If dimensions are not available, wait for image to load
    if (!imgWidth || !imgHeight) {
      // Return default dimensions if image hasn't loaded yet
      return { x: 0, y: 0, width: width, height: height }
    }
    
    const imgAspectRatio = imgWidth / imgHeight
    const stageAspectRatio = width / height
    
    let scaledWidth, scaledHeight, x, y
    
    if (imgAspectRatio > stageAspectRatio) {
      // Image is wider - fit to width
      scaledWidth = width
      scaledHeight = width / imgAspectRatio
      x = 0
      y = (height - scaledHeight) / 2
    } else {
      // Image is taller - fit to height
      scaledHeight = height
      scaledWidth = height * imgAspectRatio
      x = (width - scaledWidth) / 2
      y = 0
    }
    
    return { x, y, width: scaledWidth, height: scaledHeight }
  }, [workingImage, width, height])

  // load image
  useEffect(() => {
    if (originalFile) {
      setBusy()
      const img = new window.Image()
      img.crossOrigin = 'anonymous'
      img.src = originalFile.url
      
      img.onload = () => {
        if (isMountedRef.current) {
          setOriginalImage(img)
          baseScaleRef.current = 1
          setScale(1)
          setWorkingImage(img)
          clearBusy()
        }
      }
      
      img.onerror = () => {
        if (isMountedRef.current) {
          clearBusy()
        }
      }
      
      return () => {
        // Clean up image loading
        img.onload = null
        img.onerror = null
      }
    }
  }, [originalFile])

  // history helpers
  const snapShotState = useCallback(
    (override = {}) => ({
      items,
      scale,
      workingImage,
      cropData,
      ...override
    }),
    [items, scale, workingImage, cropData]
  )

  const pushHistory = useCallback(
    (snapshot) => {
      setHistory((h) => {
        const newH = h.slice(0, histIndex + 1).concat(snapshot)
        if (newH.length > maxHistory) newH.shift()
        return newH
      })
      setHistIndex((i) => i === -1 ? 0 : Math.min(i + 1), maxHistory - 1)
    },
    [histIndex]
  )

  useEffect(() => {
    // initialize history
    if (originalFile) {
      const img = new window.Image()
      img.crossOrigin = 'anonymous'
      img.src = originalFile.url

      img.onload = () => {
        if (isMountedRef.current) {
          pushHistory({ items: [], workingImage: img, cropData: null })
        }
      }

      img.onerror = () => {
        // Handle error if needed
      }

      return () => {
        img.onload = null
        img.onerror = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // call whenever items or scale changes to record a snapshot
  // useEffect(() => {
  // don't push when history navigation in progress we rely on explicit push
  // We'll push manually on actions to reduce noise.
  // }, [items, scale])

  // selection transformer
  useEffect(() => {
    const tr = trRef.current
    const layer = layerRef.current
    if (!tr || !layer) return
    const nodes = selectedIds.map((id) => layer.findOne(`#node-${id}`)).filter(Boolean)
    tr.nodes(nodes)
    tr.getLayer().batchDraw()
    tr.visible(nodes.length > 0)
  }, [selectedIds, items])

  // keyboard handlers
  useEffect(() => {
    const onKey = (e) => {
      // Delete / Backspace
      if (editingText) return
      if (e.key === 'Delete' || e.key === 'Backspace') {
        onDelete()
      }
      // Undo: Ctrl/Cmd + Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault()
        undo()
      }
      // Redo: Ctrl/Cmd + Shift + Z OR Ctrl+Y
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
        e.preventDefault()
        redo()
      }
      // Duplicate: Ctrl/Cmd + D
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault()
        onDuplicate()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [editingText, selectedIds, items, history, histIndex, pushHistory])

  // stage mouse interactions
  const stagePointerPos = () => stageRef.current.getPointerPosition()
  
  // Helper function to check if point is within image bounds
  const isPointInImageBounds = (x, y) => {
    if (!workingImage || !imageDimensions.width || !imageDimensions.height) return false
    return (
      x >= imageDimensions.x &&
      x <= imageDimensions.x + imageDimensions.width &&
      y >= imageDimensions.y &&
      y <= imageDimensions.y + imageDimensions.height
    )
  }

  const handleStageMouseDown = (e) => {
    const pos = stagePointerPos()
    if (tool === 'text' && e.target === stageRef.current) {
      const pos = stagePointerPos()
      // Only allow text if clicked within image bounds
      if (!isPointInImageBounds(pos.x, pos.y)) {
        return
      }
      addTextAt(pos.x, pos.y)
      return
    }
    if (!pos) return
    
    // Check if click is within image bounds (not just stage bounds)
    if (!isPointInImageBounds(pos.x, pos.y)) {
      return
    }
    
    const stage = stageRef.current

    const clickedOnTransformer =
      e.target.getParent()?.className === 'Transformer' ||
      e.target.className === 'Transformer'
    if (clickedOnTransformer) return
    // drawing tools:
    if (tool === 'rect' || tool === 'arrow') {
      const id = uid()
      setIsDrawing(true)
      if (tool === 'rect') {
        const newItem = {
          id,
          type: 'rect',
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
          stroke: color,
          strokeWidth: 2,
          fill: 'transparent',
          draggable: true,
        }
        setItems((p) => p.concat(newItem))
        setDrawing({ id, type: 'rect', startX: pos.x, startY: pos.y })
        setSelectedIds([id])
      } else {
        const newItem = {
          id,
          type: 'arrow',
          points: [pos.x, pos.y, pos.x, pos.y],
          stroke: color,
          strokeWidth: 3,
          pointerWidth: 12,
          pointerLength: 12,
          draggable: true,
        }
        setItems((p) => p.concat(newItem))
        setDrawing({ id, type: 'arrow' })
        setSelectedIds([id])
      }
      return
    }

    // If clicked empty area -> start marquee selection (only if within image bounds)
    const clickedOnEmpty =
      e.target === stage ||
      e.target === stage.findOne('Layer')
    if (clickedOnEmpty) {
      setMarquee({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
        startX: pos.x,
        startY: pos.y,
        visible: true,
      })
      setSelectedIds([])
      return
    }

    // clicked an object -> select (support shift)
    const node = e.target
    const nodeIdRaw = node.id() // 'node-<id>'
    const id = nodeIdRaw && nodeIdRaw.startsWith('node-') ? nodeIdRaw.slice(5) : nodeIdRaw
    const isShift = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey
    if (isShift) {
      setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    } else {
      setSelectedIds([id])
    }
  }

  const handleStageMouseMove = (e) => {
    const pos = stagePointerPos()
    if (!pos) return
    // update drawing object
    if (isDrawing && drawing) {
      if (drawing.type === 'rect') {
        // Constrain rectangle drawing to image bounds
        const imgBounds = imageDimensions
        const constrainedX = Math.max(imgBounds.x, Math.min(pos.x, imgBounds.x + imgBounds.width))
        const constrainedY = Math.max(imgBounds.y, Math.min(pos.y, imgBounds.y + imgBounds.height))
        
        setItems((prev) => prev.map((it) => (
          it.id === drawing.id
            ? {
                ...it,
                x: drawing.startX,
                y: drawing.startY,
                width: constrainedX - drawing.startX,
                height: constrainedY - drawing.startY
              }
            : it)))
      } else if (drawing.type === 'arrow') {
        // Constrain arrow drawing to image bounds
        const imgBounds = imageDimensions
        const constrainedX = Math.max(imgBounds.x, Math.min(pos.x, imgBounds.x + imgBounds.width))
        const constrainedY = Math.max(imgBounds.y, Math.min(pos.y, imgBounds.y + imgBounds.height))
        
        setItems((prev) => prev.map((it) => (
          it.id === drawing.id
            ? {
                ...it,
                points: [it.points[0],
                  it.points[1],
                  constrainedX,
                  constrainedY]
              }
            : it)))
      }
      return
    }
    // update marquee (constrain to image bounds)
    if (marquee && marquee.visible) {
      const imgBounds = imageDimensions
      const startX = marquee.startX
      const startY = marquee.startY
      const constrainedX = Math.max(imgBounds.x, Math.min(pos.x, imgBounds.x + imgBounds.width))
      const constrainedY = Math.max(imgBounds.y, Math.min(pos.y, imgBounds.y + imgBounds.height))
      const x = Math.min(startX, constrainedX)
      const y = Math.min(startY, constrainedY)
      const w = Math.abs(constrainedX - startX)
      const h = Math.abs(constrainedY - startY)
      setMarquee({ ...marquee, x, y, width: w, height: h })
    }
  }
  const handleStageMouseUp = (e) => {
    // finish drawing
    if (isDrawing && drawing) {
      setIsDrawing(false)
      setDrawing(null)
      pushHistory(snapShotState({ items }))
      return
    }

    if (marquee && marquee.visible) {
      const layer = layerRef.current
      const sel = []
      const mRect = { x: marquee.x, y: marquee.y, width: marquee.width, height: marquee.height }
      items.forEach((it) => {
        const node = layer.findOne(`#node-${it.id}`)
        if (!node) return
        const bbox = node.getClientRect()
        if (rectsIntersect(bbox, mRect)) sel.push(it.id)
      })
      setSelectedIds(sel)
      setMarquee(null)
    }
  }

  // text editing overlay
  const editText = (it) => {
    if (!isMountedRef.current) return
    
    const stage = stageRef.current
    const layer = layerRef.current
    const node = layer.findOne(`#node-${it.id}`)
    if (!node) return
    
    if (!isMountedRef.current) return
    setEditingText(true)
    const abs = node.getAbsolutePosition()
    const textarea = document.createElement('textarea')
    textarea.value = it.text
    textarea.style.position = 'absolute'
    const containerRect = stage.container().getBoundingClientRect()
    textarea.style.top = containerRect.top + window.scrollY + abs.y + 'px'
    textarea.style.left = containerRect.left + window.scrollX + abs.x + 'px'
    textarea.style.fontSize = it.fontSize + 'px'
    textarea.style.padding = '4px'
    textarea.style.border = '1px solid #ccc'
    textarea.style.background = 'white'
    textarea.style.color = it.fill || color
    containerRef.current.appendChild(textarea)
    textarea.focus()
    const remove = () => {
      if (!isMountedRef.current) {
        if (containerRef.current && textarea.parentNode === containerRef.current) {
          containerRef.current.removeChild(textarea)
        }
        return
      }
      const newText = textarea.value
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, text: newText } : x)))
      if (containerRef.current && textarea.parentNode === containerRef.current) {
        containerRef.current.removeChild(textarea)
      }
      setEditingText(false)
      pushHistory(snapShotState({ items }))
    }
    textarea.addEventListener('blur', remove)
    textarea.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault()
        textarea.blur()
      }
    })
  }

  // transform/drag end save
  const onTransformEnd = (id) => {
    const layer = layerRef.current
    const node = layer.findOne(`#node-${id}`)
    if (!node) return
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it
        const rotation = node.rotation()
        if (it.type === 'rect') {
          const width = Math.max(1, node.width() * node.scaleX())
          const height = Math.max(1, node.height() * node.scaleY())
          node.scaleX(1)
          node.scaleY(1)
          return { ...it, x: node.x(), y: node.y(), width, height, rotation }
        }
        if (it.type === 'text') {
          return { ...it, x: node.x(), y: node.y(), rotation, fontSize: node.fontSize() }
        }
        if (it.type === 'arrow') {
          return { ...it, points: node.points(), rotation }
        }
        return it
      })
    )
    pushHistory(snapShotState({ items }))
  }

  const onDragEnd = (id) => {
    const node = layerRef.current.findOne(`#node-${id}`)
    if (!node) return
    setItems((prev) => prev.map((it) => (
      it.id === id
        ? {
            ...it,
            x: node.x(),
            y: node.y(),
            points: node.points?.() || it.points
          }
        : it)))
    pushHistory(snapShotState({ items }))
  }
  
  // tool actions
  const addTextAt = (x, y) => {
    const id = uid()
    const it = { id, type: 'text', x, y, text: 'Double-click to edit', fontSize, fill: color, draggable: true }
    const next = items.concat(it)
    setItems(next)
    setSelectedIds([id])
    pushHistory(snapShotState({ items: next }))
  }

  const zoomTo = (factor) => {
    const s = Math.max(0.2, Math.min(4, scale * factor))
    setScale(s)
    const stage = stageRef.current
    stage.scale({ x: s, y: s })
    stage.batchDraw()
    pushHistory(snapShotState({ items, scale: s }))
  }

  // Utility: convert base64 to File
  function dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }

    return new File([u8arr], filename, { type: mime })
  }

  const resizeImage = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        1500, // Max width
        1500, // Max height
        'JPEG', // Output format
        80, // Quality
        0, // Rotation
        (uri) => resolve(uri), // Callback with resized image (base64)
        'base64'
      )
    })

  const withHiddenTransformer = async (fnc) => {
    const transform = trRef?.current
    if (!transform) {
      await fnc()
      return
    }
    transform?.visible(false)
    transform?.getLayer()?.batchDraw()
    try {
      await fnc()
    } finally {
      transform?.visible(true)
      transform?.getLayer()?.batchDraw()
    }
  }
  
  const resetEditorState = useCallback(() => {
    setItems([])
    setSelectedIds([])
    setHistory([])
    setHistIndex(-1)
    setCropData(null)
    setScale(1)
    setIsCropping(false)
    baseScaleRef.current = 1
    setWorkingImage(null)
    setOriginalImage(null)
    trRef.current?.nodes([])
    trRef.current?.getLayer()?.batchDraw()
  }, [])

  const exportCompressed = async () => {
    if (!isMountedRef.current) return
    
    setBusy()
    resetZoom()
    const stage = stageRef.current

    // export as dataURL (PNG first to detect bounds), then crop to image bounds and convert to JPG
    await withHiddenTransformer(async () => {
      if (!isMountedRef.current) {
        clearBusy()
        return
      }
      
      // Export as PNG first to detect content bounds (need transparency info for detection)
      const pngDataUrl = stage.toDataURL({ x: 0, y: 0, width: width, height: height, pixelRatio: 2, mimeType: 'image/png' })
      
      // Load the image to get its dimensions and find content bounds
      const img = new Image()
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = pngDataUrl
      })
      
      if (!isMountedRef.current) {
        clearBusy()
        return
      }
      
      // Create a temporary canvas to analyze the image and find content bounds
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = img.width
      tempCanvas.height = img.height
      const tempCtx = tempCanvas.getContext('2d')
      tempCtx.drawImage(img, 0, 0)
      
      // Get image data to find non-transparent bounds
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
      const data = imageData.data
      
      // Find the bounding box of non-transparent pixels
      let minX = tempCanvas.width
      let minY = tempCanvas.height
      let maxX = 0
      let maxY = 0
      
      for (let y = 0; y < tempCanvas.height; y++) {
        for (let x = 0; x < tempCanvas.width; x++) {
          const alpha = data[(y * tempCanvas.width + x) * 4 + 3]
          if (alpha > 0) { // Non-transparent pixel
            minX = Math.min(minX, x)
            minY = Math.min(minY, y)
            maxX = Math.max(maxX, x)
            maxY = Math.max(maxY, y)
          }
        }
      }
      
      // If no content found, use full image
      if (minX >= maxX || minY >= maxY) {
        minX = 0
        minY = 0
        maxX = tempCanvas.width
        maxY = tempCanvas.height
      }
      
      // Create final canvas with only the content area (no transparent padding)
      const finalCanvas = document.createElement('canvas')
      const contentWidth = maxX - minX + 1
      const contentHeight = maxY - minY + 1
      finalCanvas.width = contentWidth
      finalCanvas.height = contentHeight
      const finalCtx = finalCanvas.getContext('2d')
      
      // Draw only the content area
      finalCtx.drawImage(
        img,
        minX, minY, contentWidth, contentHeight,
        0, 0, contentWidth, contentHeight
      )
      
      // Export as JPG (cropped to content bounds, no background padding)
      const finalDataUrl = finalCanvas.toDataURL('image/jpeg', 0.92)
      const file = dataURLtoFile(finalDataUrl, originalFile?.file?.name || 'edited.jpg')

      // Resize if needed, using JPG format
      const resizedBase64 = await new Promise((resolve) => {
        Resizer.imageFileResizer(
          file,
          1500, // Max width
          1500, // Max height
          'JPEG', // Use JPEG format
          80, // Quality
          0, // Rotation
          (uri) => resolve(uri), // Callback with resized image (base64)
          'base64'
        )
      })
      
      if (!isMountedRef.current) {
        clearBusy()
        return
      }
      
      // Convert resized base64 back to file
      const resizedFile = dataURLtoFile(resizedBase64, originalFile?.file?.name || 'edited.jpg')
      const validImage = []
      const imgURL = URL.createObjectURL(resizedFile)
      validImage.push({
        id: Math.random().toString(36).substr(2, 9),
        file: resizedFile,
        url: imgURL
      })

      if (isMountedRef.current) {
        onImageCapture(validImage[0].file)
        setSelectedImageLabel(originalFile?.file?.name || 'referenceImage.jpg')
        // setState({ showRef: false })
        updateRefState(false)
        setMode('upload')
        setWorkingImage(null)
      }
    })
    
    if (isMountedRef.current) {
      clearBusy()
      onClose()
    }
  }

  // Render shape helper
  const clamp = (val, min, max) => Math.max(min, Math.min(max, val))
  const renderItem = (it) => {
    const imgBounds = imageDimensions
    const common = {
      id: `node-${it.id}`,
      key: it.id,
      // listening: false,
      draggable: !!it.draggable,
      dragBoundFunc: (pos) => {
        // Constrain dragging to image bounds instead of stage bounds
        if (!imgBounds.width || !imgBounds.height) {
          return { x: clamp(pos.x, 0, width), y: clamp(pos.y, 0, height) }
        }
        
        // For rectangles, also constrain by width/height to keep them within bounds
        if (it.type === 'rect') {
          const itemWidth = it.width || 0
          const itemHeight = it.height || 0
          return {
            x: clamp(pos.x, imgBounds.x, imgBounds.x + imgBounds.width - itemWidth),
            y: clamp(pos.y, imgBounds.y, imgBounds.y + imgBounds.height - itemHeight)
          }
        }
        
        // For text and arrows, just constrain the position
        return {
          x: clamp(pos.x, imgBounds.x, imgBounds.x + imgBounds.width),
          y: clamp(pos.y, imgBounds.y, imgBounds.y + imgBounds.height)
        }
      },
      onClick: (e) => {
        const isShift = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey
        if (isShift) {
          setSelectedIds((prev) => (prev.includes(it.id) ? prev : [...prev, it.id]))
        } else {
          setSelectedIds([it.id])
        }
      },
      onDragEnd: () => onDragEnd(it.id),
      onTransformEnd: () => onTransformEnd(it.id),
    }
    if (it.type === 'rect') {
      const w = it.width || 0
      const h = it.height || 0
      return <Rect {...common} x={it.x} y={it.y} width={w} height={h} rotation={it.rotation || 0} stroke={it.stroke}
        strokeWidth={it.strokeWidth} fill={it.fill} />
    }
    if (it.type === 'text') {
      return <Text {...common} x={it.x} y={it.y} text={it.text} fontSize={it.fontSize} rotation={it.rotation || 0} fill={it.fill}
        onDblClick={() => editText(it)} />
    }
    if (it.type === 'arrow') {
      return <Arrow {...common} points={it.points} rotation={it.rotation || 0} stroke={it.stroke} strokeWidth={it.strokeWidth}
        pointerWidth={it.pointerWidth} pointerLength={it.pointerLength} hitStrokeWidth={20}/>
    }
    return null
  }

  const applyCrop = () => {
    if (!isMountedRef.current) return
    
    setBusy()
    if (!cropInstance) {
      if (isMountedRef.current) clearBusy()
      return
    }
    const canvas = cropInstance.getCroppedCanvas({
      maxWidth: 2000,
      maxHeight: 2000,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    })

    if (!canvas) {
      if (isMountedRef.current) clearBusy()
      return
    }

    canvas.toBlob((blob) => {
      if (!blob || !isMountedRef.current) {
        if (isMountedRef.current) clearBusy()
        return
      }

      const img = new Image()
      const url = URL.createObjectURL(blob)
      img.src = url
      img.onload = () => {
        if (isMountedRef.current) {
          setWorkingImage(img)
          baseScaleRef.current = 1
          setScale(1)
          setCropData(cropData)
          setIsCropping(false)
          pushHistory(
            snapShotState({
              items,
              workingImage: img,
              cropData
            })
          )
          clearBusy()
        }
        URL.revokeObjectURL(url)
      }
      img.onerror = () => {
        if (isMountedRef.current) {
          clearBusy()
        }
        URL.revokeObjectURL(url)
      }
    }, 'image/png')
  }

  const onStartCrop = () => {
    setBusy()
    setIsCropping(true)
    // requestAnimationFrame(() => {
    //   requestAnimationFrame(() => {
    //     cropInstance?.resize()
    //     cropInstance?.zoomTo(1)
    //   })
    // })
  }

  const onFullImg = () => {
    if (!isMountedRef.current) return
    
    setBusy()
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.src = originalFile.url
    
    img.onload = () => {
      if (isMountedRef.current) {
        setOriginalImage(img)
        setWorkingImage(img)
        clearBusy()
      }
    }
    
    img.onerror = () => {
      if (isMountedRef.current) {
        clearBusy()
      }
    }
  }

  const resetZoom = () => {
    const scl = baseScaleRef.current
    setScale(scl)
    const stage = stageRef.current
    stage.scale({ x: scl, y: scl })
    stage.batchDraw()
    pushHistory(snapShotState({ scale: scl }))
  }

  const onDuplicate = () => {
    if (!selectedIds.length) return
    const clones = selectedIds.map(id => {
      const src = items.find(i => i.id === id)
      if (!src) return null
      const copy = JSON.parse(JSON.stringify(src))
      copy.id = uid()
      if (copy.x !== undefined) copy.x += 10
      if (copy.y !== undefined) copy.y += 10
      if (copy.points) copy.points = copy.points.map((p, i) => p + 10)
      return copy
    }).filter(Boolean)
    if (clones.length) {
      const next = items.concat(clones)
      setItems(next)
      setSelectedIds(clones.map(c => c.id))
      pushHistory(snapShotState({ items: next }))
    }
  }

  const onDelete = () => {
    if (!selectedIds.length) return
    const next = items.filter(it => !selectedIds.includes(it.id))
    setItems(next)
    setSelectedIds([])
    pushHistory(snapShotState({ items: next }))
  }

  const undo = () => {
    if (histIndex <= 0) return
    const prev = histIndex - 1
    const snap = history[prev]
    if (!snap) return
    setItems(snap.items)
    setScale(snap.scale)
    setWorkingImage(snap.workingImage)
    setCropData(snap.cropData)
    setHistIndex(prev)
    setSelectedIds([])
  }

  const redo = () => {
    if (histIndex >= history.length - 1) return
    const nextIdx = histIndex + 1
    const snap = history[nextIdx]
    if (!snap) return
    setItems(snap.items)
    setScale(snap.scale)
    setWorkingImage(snap.workingImage)
    setCropData(snap.cropData)
    setHistIndex(nextIdx)
    setSelectedIds([])
  }

  const cancelCrop = () => {
    clearBusy()
    setIsCropping(false)
  }

  // CSS Part
  const headerStyle = {
    display: 'flex',
    borderBottom: '1px solid #ddd'
  }

  const dialogSize = useMemo(() => {
    switch (mode) {
      case 'upload':
        return {
          display: 'flex',
          width: '500px',
          height: '500px',
          // resize: 'both', not needed to resize
          overflow: 'hidden',
        }
      case 'refImg':
        return {
          width: '1080px',
          height: '600px',
          background: '#fff',
          borderRadius: 1,
          display: 'flex',
          overflow: 'hidden',
          flexDirection: 'column'
        }
    }
  }, [mode])

  const tab = {
    flex: 1,
    padding: '10px',
    cursor: 'pointer',
    border: 'none',
    background: '#fff',
  }

  const tabActive = {
    ...tab,
    background: '#f5f5f5',
    fontWeight: 'bold'
  }

  // just for camera loader
  useEffect(() => {
    if (activeTab === 'camera') {
      setBusy()
    }
    if (activeTab !== 'camera') {
      clearBusy()
      return
    }
    
    let timeoutId1 = null
    let timeoutId2 = null
    
    if (cameraLoaderRef.current) {
      timeoutId1 = setTimeout(() => {
        clearBusy()
      }, 1500)
    } else {
      timeoutId2 = setTimeout(() => {
        clearBusy()
        cameraLoaderRef.current = true
      }, 3000)
    }
    
    return () => {
      if (timeoutId1) clearTimeout(timeoutId1)
      if (timeoutId2) clearTimeout(timeoutId2)
      clearBusy()
    }
  }, [activeTab])

  const handleFilesChange = (e) => {
    if (!isMountedRef.current) return
    
    resetEditorState()
    const file = e.target.files[0]
    if (!file) return
    const validImage = []
    const maxSizeInBytes = maxSize * 1024 * 1024
    const isFileSizeExceeded = doesFileSizeExceed(file.size, maxSizeInBytes)
    const fileType = file.type.toLowerCase()
    const fileName = file.name.toLowerCase()
    
    if (!isMountedRef.current) return
    
    if (allowedFormats.includes('JPG') && allowedFormats.includes('JPEG') && !fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') && fileType !== 'image/jpeg' && isFileSizeExceeded) {
      if (isMountedRef.current) {
        setError(t('demo.uploadJpgTypeandSizeErr', { noOfMb: maxSize }))
      }
      return
    } else if (allowedFormats.includes('JPG') && allowedFormats.includes('JPEG') && !fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') && fileType !== 'image/jpeg') {
      if (isMountedRef.current) {
        setError(t('demo.uploadJpgType'))
      }
      return
    } else if (isFileSizeExceeded) {
      if (isMountedRef.current) {
        setError(t('demo.fileSizeExceeded', { noOfMb: maxSize }))
      }
      return
    }

    validImage.push({
      id: Math.random().toString(36).substr(2, 9),
      file,
      url: URL.createObjectURL(file),
    })
    
    if (isMountedRef.current) {
      updateRefState(true)
      setError('')
      setOriginalFile(validImage[0])
      setActiveTab('upload')
      setMode('refImg')
      e.target.value = null
    }
  }

  const handleTakePhoto = async (dataUri) => {
    if (!isMountedRef.current) return
    
    resetEditorState()
    try {
      setBusy()
      // Convert dataUri (base64) to File
      const newFile = dataURLtoFile(dataUri, 'photo.jpg')
      const validImage = []
      const imgURL = URL.createObjectURL(newFile)
      validImage.push({
        id: Math.random().toString(36).substr(2, 9),
        newFile,
        url: imgURL
      })
      
      if (isMountedRef.current) {
        updateRefState(true)
        setOriginalFile(validImage[0])
        setActiveTab('upload')
        setMode('refImg')
        clearBusy()
      }
    } catch (err) {
      if (isMountedRef.current) {
        console.error(err.message)
        clearBusy()
      }
    }
  }

  const onCancel = async () => {
    resetEditorState()
    setMode('upload')
    setActiveTab('upload')
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return
        if (reason === 'escapeKeyDown') return
        onClose()
      }}
      scroll="paper" // for scrolloling
      PaperProps={{
        sx: {
          ...dialogSize,
          maxWidth: 'none',
          transition: 'all 0.3s ease',
        }
      }}
      aria-labelledby="scroll-dialog-title"
    >
      <DialogTitle id="scroll-dialog-title">{t('smartCatalog.refImage')}</DialogTitle>

      {/* --- FIXED SECTION --- */}
      {!showEditScreen &&
        <div>
          <div style={headerStyle}>
            <button
              style={activeTab === 'upload' ? tabActive : tab}
              onClick={() => setActiveTab('upload')}
            >
              Upload
            </button>
            <button
              style={activeTab === 'camera' ? tabActive : tab}
              onClick={() => setActiveTab('camera')}
            >
              Camera
            </button>
          </div>
        </div>
      }
      {showEditScreen && (
        <div style={{ display: 'flex', gap: 8, padding: 8, borderBottom: '1px solid #ddd', background: '#fff', marginBottom: 8, alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <Tooltip
              title="Text"
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px', marginLeft: '30px' }}
            ><IconButton onClick={() => setTool('text')} sx={{ color: '#000' }}><AbcIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Rectangle'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={() => setTool('rect')} sx={{ color: '#000' }}><Crop169Icon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Arrow'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={() => setTool('arrow')} sx={{ color: '#000' }}><ArrowOutwardIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Select'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={() => setTool(null)} sx={{ color: '#000' }}><HighlightAltIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Crop'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={onStartCrop} sx={{ color: '#000' }}><CropIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='FullSize'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={onFullImg} sx={{ color: '#000' }}><CropFreeIcon /></IconButton>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 12, flexShrink: 0 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              Color <input type='color' value={color} onChange={(e) => setColor(e.target.value)} />
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              Font <input type='number' min={10} max={72} value={fontSize} onChange={(e) =>
              setFontSize(Number(e.target.value))} style={{ width: 64 }} />
            </label>
            <Tooltip
              title='ZoomIn'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={() => zoomTo(1.15)} sx={{ color: '#000' }}><ZoomInIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='ResetZoom'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={resetZoom} sx={{ color: '#000' }}><RestartAltIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='ZoomOut'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={() => zoomTo(1 / 1.15)} sx={{ color: '#000' }}><ZoomOutIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Duplicate'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={onDuplicate} sx={{ color: '#000' }}><ControlPointDuplicateIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Delete'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={onDelete} sx={{ color: '#000' }}><DeleteIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Undo'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={undo} sx={{ color: '#000' }}><UndoIcon /></IconButton>
            </Tooltip>
            <Tooltip
              title='Redo'
              placement="top"
              slots={{
                transition: Fade,
              }}
              slotProps={{
                transition: { timeout: 600 },
              }}
              style={{ width: '40px', margin: '5px' }}
            ><IconButton onClick={redo} sx={{ color: '#000' }}><RedoIcon /></IconButton>
            </Tooltip>
          </div>
        </div>
      )}
      <DialogContent dividers={true}>
        <DialogContentText
          component="div"
          id="scroll-dialog-description"
          // ref={descriptionElementRef}
          tabIndex={-1}
        >
          <>
          <div style={{ display: 'flex', width: '100%' }}>
            {!showEditScreen && (
                <>
                  {activeTab === 'camera' && (
                    <div style={{
                      width: '500px',
                      height: '300px',
                      resize: 'both',
                      overflow: 'hidden',
                      // display: activeTab === 'camera' ? 'block' : 'none'
                    }}>
                      <Camera
                        onTakePhoto={(dataUri) => { handleTakePhoto(dataUri) }}
                        isMaxResolution={true}
                        // ref={cameraReff}
                        sizeFactor={1}
                        imageType='jpg'
                        imageCompression={0}
                      />
                    </div>
                  )}
                  {activeTab === 'upload' && (
                    <div style={{
                      width: '500px',
                      height: '300px',
                      resize: 'both',
                      overflow: 'hidden',
                      // display: activeTab === 'upload' ? 'block' : 'none'
                    }}>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg"
                        onChange={handleFilesChange}
                        style={{ marginBottom: 10 }}
                      />
                      {error && <div style={{ color: 'red', marginBottom: 10 }}>{error}</div>}
                    </div>)}
                </>
            )}
              {showEditScreen && (
                <div ref={containerRef} style={{
                  height: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0,
                  fontFamily: 'Inter, Roboto, sans-serif'
                }}>
                  <div style={{ flex: 1, background: '#f5f5f5', padding: 12 }}>
                    <div style={{ border: '1px solid #ddd', display: 'inline-block' }}>
                      <Stage
                        width={width}
                        height={height}
                        ref={stageRef}
                        scaleX={scale}
                        scaleY={scale}
                        listening={!isCropping}
                        onMouseDown={(e) => {
                          handleStageMouseDown(e)
                        }}
                        onMouseMove={handleStageMouseMove}
                        onMouseUp={handleStageMouseUp}
                      >
                        <Layer ref={layerRef}>
                          {/* background */}
                          <Group
                            clipFunc={(ctx) => {
                              ctx.rect(0, 0, width, height)
                            }}
                          >
                            {workingImage && <KonvaImage image={workingImage} x={imageDimensions.x} y={imageDimensions.y} width={imageDimensions.width} height={imageDimensions.height} listening={false} />}
                            {/* items */}
                            {items.map((it) => renderItem(it))}
                            {/* marquee */}
                            {marquee && marquee.visible && (
                              <Rect
                                x={marquee.x}
                                y={marquee.y}
                                width={marquee.width}
                                height={marquee.height}
                                stroke='rgba(0,120,255,0.7)'
                                dash={[6, 4]}
                                fill='rgba(0,120,255,0.08)'
                                listening={false}
                              />
                            )}
                          </Group>
                          <Transformer
                            ref={trRef}
                            rotateEnabled={true}
                            rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                            rotationAnchorOffset={30}
                            enabledAnchors={[
                              'top-left',
                              'top-right',
                              'bottom-left',
                              'bottom-right',
                              'middle-left',
                              'middle-right',
                              'top-center',
                              'bottom-center',
                            ]}
                          />
                        </Layer>
                      </Stage>
                    </div>
                  </div>
                </div>
              )}
              {showEditScreen && isCropping && workingImage && (
                createPortal(
                  <div
                    style={{
                      position: 'fixed',
                      inset: 0,
                      zIndex: 9999,
                      background: 'rgba(0,0,0,0.75)',
                      display: isCropping ? 'flex' : 'none',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <div style={{
                      width: '1000px',
                      height: '450px',
                      background: '#fff',
                      borderRadius: 3,
                      display: 'flex',
                      overflow: 'hidden',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        flex: 1,
                        position: 'relative',
                        overflow: 'hidden',
                        background: '#111'
                      }}>
                        <Cropper
                          style={{ height: '100%', width: '100%' }}
                          initialAspectRatio={1}
                          src={workingImage.src}
                          viewMode={1}
                          autoCropArea={1}
                          background={false}
                          responsive={false}
                          checkOrientation={false}
                          ready={() => {
                            clearBusy()
                          }}
                          onInitialized={(instance) => {
                            setCropInstance(instance)
                          }}
                          ref={cropperRef}
                        />
                      </div>
                      <div style={{ marginTop: 4, marginBottom: 4, marginRight: 4, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <OutlinedButton onClick={cancelCrop}>forms.cancel</OutlinedButton>
                        <ContainedButton onClick={applyCrop}>buttons.crop</ContainedButton>
                      </div>
                    </div>
                  </div>,
                  document.body
                )
              )}
            </div>
          </>
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <CancelButton id={'Cancel_button_cancel'} arrRefs={props.arrRefs} onClick={onCancel} />
        {showEditScreen && (
          <ContainedButton onClick={exportCompressed}>buttons.save</ContainedButton>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ReferenceImage